import ContextualBar from '@/components/ContextualBar';
import CriticalBanner from '@/components/CriticalBanner';
import CustomCard from '@/components/customCard';
import CustomFullscreenBar from '@/components/CustomFullscreenBar';
import GridLayoutPreview from '@/components/GridLayoutPreview';
import Layout from '@/components/layout';
import ModalConfirmLeavePage from '@/components/ModalConfirmLeavePage';
import Preview from '@/components/Preview';
import { stickyTopCreate } from '@/constants';
import { LocationConditionEnum } from '@/constants/enum';
import { PATH } from '@/constants/path';
import { checkShowErrorInline } from '@/helpers';
import { useCustomUserIdClarity } from '@/hooks/useCustomUserIdClarity';
import useHandleContextual from '@/hooks/useHandleContextureBar';
import useToast from '@/hooks/useToast';
import useUserPlan from '@/hooks/useUserPlan';
import { CURuleStyled } from '@/pages/Settings/Components/EstimateRules/components/CURule/styled';
import { apiCaller } from '@/redux/query';
import appbarSlice, { clickedDiscardSelector, isShowContextualBarSelector } from '@/redux/slice/appbar.slice';
import ruleSlice, { initRuleSettingsSelector, ruleErrorsSelector, ruleSettingsSelector } from '@/redux/slice/rule.slice';
import { rulesSelector } from '@/redux/slice/rules.slice';
import { IItemCondition, ILocationCondition, IRule } from '@/types/newSetup.type';
import { Tabs, TabsProps } from '@shopify/polaris';
import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AdvancedSetup from './components/AdvancedSetup';
import DateAndMessage from './components/DateAndMessage';
import DisplayConditions from './components/DisplayConditions';

export type IState = IRule;

const tabs: TabsProps['tabs'] = [
  {
    id: 'step-1',
    content: (<p className="fw-500">Estimated date and message</p>) as any,
  },
  {
    id: 'step-2',
    content: <p className="fw-500">Display conditions</p>,
  },
  {
    id: 'extra',
    content: <p className="fw-500">Advanced setup</p>,
  },
];

export default function CURule() {
  useCustomUserIdClarity('CU rule');
  const { id } = useParams();
  const { show } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { limitPlan } = useUserPlan();
  const contextualBar = useHandleContextual();

  const [createRule, { isLoading: isLoadingCreate }] = apiCaller.useCreateRuleMutation();
  const [updateRule, { isLoading: isLoadingUpdate }] = apiCaller.useUpdateRuleMutation();
  // const [sendEventCustomerIo] = apiCaller.useSendEventCustomerIoMutation();

  const state = useSelector(ruleSettingsSelector);
  const initState = useSelector(initRuleSettingsSelector);
  const errors = useSelector(ruleErrorsSelector);
  const { allRules } = useSelector(rulesSelector);
  const clickedDiscard = useSelector(clickedDiscardSelector);
  const isShowContextualBar = useSelector(isShowContextualBarSelector);

  const [currentStep, setCurrentStep] = useState(0);
  // const [isVisitedStep2, setIsVisitedDateAndMessage] = useState(false);
  const [openModalConfirmLeave, setOpenModalConfirmLeave] = useState(false);

  const currentRule = useMemo(() => {
    return allRules.find((rule) => rule.id === Number(id));
  }, [allRules, id]);

  useEffect(() => {
    if (location.state?.isDefaultSelectDisplayConditionTab) {
      setCurrentStep(1);
    }
  }, [location.state?.isDefaultSelectDisplayConditionTab]);

  useEffect(() => {
    dispatch(ruleSlice.actions.clearError());

    if (id && currentRule) {
      dispatch(ruleSlice.actions.setValues({ value: currentRule }));
    }
  }, [dispatch, id, currentRule]);

  useEffect(() => {
    const check = JSON.stringify(currentRule) !== JSON.stringify(state);
    dispatch(appbarSlice.actions.handleUpdateContextualBarState({ isShowContextualBar: check }));
  }, [currentRule, state, dispatch]);

  const isDisabledDiscard = useMemo(() => {
    return isLoadingCreate || isLoadingUpdate || (!id && isEqual(initState, state));
  }, [id, initState, isLoadingCreate, isLoadingUpdate, state]);

  const content = useMemo(() => {
    switch (currentStep) {
      case 0:
        return (
          <DateAndMessage
          //  setIsVisitedDateAndMessage={setIsVisitedDateAndMessage}
          />
        );
      case 1:
        return <DisplayConditions />;
      default:
        return <AdvancedSetup />;
    }
  }, [currentStep]);

  useEffect(() => {
    if (clickedDiscard) {
      dispatch(ruleSlice.actions.clearError());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clickedDiscard]);

  const handleDiscardChanges = useCallback(() => {
    if (id) {
      const currentRule = allRules.find((rule) => rule.id === Number(id));
      if (!currentRule) return;
      dispatch(
        ruleSlice.actions.setValues({
          value: currentRule,
        }),
      );
    } else {
      dispatch(ruleSlice.actions.handleInit());
    }
    dispatch(appbarSlice.actions.handleUpdateDiscardState({ isClickedDiscard: true }));
  }, [allRules, dispatch, id]);

  const handleCreateUpdateRule = useCallback(async () => {
    // const listConditionMetafieldKeys = [
    //   ItemConditionEnum.ExcludeProductMetafield,
    //   ItemConditionEnum.IncludeProductMetafield,
    //   ItemConditionEnum.ExcludeVariantMetafield,
    //   ItemConditionEnum.IncludeVariantMetafield,
    // ];
    const handleRemoveEmptyData = (conditions: IItemCondition[] | ILocationCondition[]) => {
      let res = conditions.map((condition) => {
        // if (listConditionMetafieldKeys.includes(condition.type as ItemConditionEnum)) {
        //   return {
        //     ...condition,
        //     value: condition.value?.map((value) => ({ ...value, data: JSON.stringify(value?.data) })),
        //   };
        // }
        return {
          ...condition,
          value: condition.value?.filter((value) => value?.data),
        };
      });

      res = res.filter((item) => item.value.length);

      return res;
    };

    const itemConditions = handleRemoveEmptyData(state.itemConditions) as IItemCondition[];
    const locationConditions = handleRemoveEmptyData(state.locationConditions) as ILocationCondition[];
    let newState = {
      ...state,
      itemConditions,
      locationConditions,
    };
    if (limitPlan) {
      const zipcodeCondition = state.locationConditions.find((condition) => condition.type === LocationConditionEnum.Zipcode);
      if (zipcodeCondition && zipcodeCondition?.value[0]?.data?.split(',')?.length > limitPlan.maxZipcode) {
        const temp = [...zipcodeCondition?.value[0]?.data?.split(',')];
        newState = {
          ...newState,
          locationConditions: [
            ...newState.locationConditions.filter((condition) => condition.type !== LocationConditionEnum.Zipcode),
            {
              type: LocationConditionEnum.Zipcode,
              value: [
                {
                  data: temp.slice(0, limitPlan.maxZipcode).join(','),
                },
              ],
            },
          ],
        };
      }
    }
    const mutation = id ? updateRule : createRule;
    mutation(newState).then((res) => {
      const checkError = checkShowErrorInline(res);
      if (checkError.status) {
        window.scrollTo(0, 0);
        dispatch(ruleSlice.actions.handleSetError({ value: { field: 'serverError', msg: checkError.msg } }));
      } else {
        show(`Rule ${id ? 'updated' : 'created'}`);
        // if (isVisitedStep2) {
        //   sendEventCustomerIo({ event: 'set_rule' });
        // }
        navigate((!id && location.state?.previousPath) || location.state?.from || PATH.RULE_SETUP);
      }
    });
  }, [
    state,
    limitPlan,
    id,
    updateRule,
    createRule,
    dispatch,
    show,
    navigate,
    location.state?.previousPath,
    location.state?.from,
  ]);

  const handleClickBackAction = useCallback(() => {
    dispatch(appbarSlice.actions.handleUpdateContextualBarState({ isShowContextualBar: false }));
    dispatch(ruleSlice.actions.handleInit());
    navigate(location?.state?.from || PATH.RULE_SETUP);
    contextualBar?.hide();
    dispatch(ruleSlice.actions.handleResetIsDefaultOpenModalAddCondition());
  }, [contextualBar, dispatch, location?.state?.from, navigate]);

  const headerContent = useMemo(
    () => (
      <div className="w-full main-navbar">
        <CustomFullscreenBar
          onAction={() => {
            isShowContextualBar ? setOpenModalConfirmLeave(true) : handleClickBackAction();
          }}
        >
          <div style={{ minWidth: '85px' }}>
            <b style={{ fontSize: '20px' }}>{id ? 'Edit rule' : 'New rule'}</b>
          </div>
        </CustomFullscreenBar>
      </div>
    ),
    [id, isShowContextualBar, handleClickBackAction],
  );

  const errorBanner = useMemo(() => {
    const serverError = errors.find((error) => error.field === 'serverError');
    if (!serverError) return null;

    return (
      <div className="wrap-banner-error">
        <CustomCard>
          <CriticalBanner isVisible={true}>{serverError.msg}</CriticalBanner>
        </CustomCard>
      </div>
    );
  }, [errors]);

  return (
    <CURuleStyled>
      <Layout headers={headerContent}>
        <ContextualBar
          isVisible={isShowContextualBar}
          saveAction={handleCreateUpdateRule}
          loadingSave={isLoadingCreate || isLoadingUpdate}
          isDisabled={!!errors.length || isLoadingCreate || isLoadingUpdate}
          isDisabledDiscard={isDisabledDiscard}
          discardAction={handleDiscardChanges}
        />
        <div>
          {errorBanner}
          <div className="tabs-container">
            <Tabs
              tabs={tabs}
              selected={currentStep}
              onSelect={(selectedTabIndex) => {
                setCurrentStep(selectedTabIndex);
              }}
              fitted
            ></Tabs>
          </div>
        </div>
        <GridLayoutPreview>
          <div>{content}</div>
          <Preview top={stickyTopCreate} defaultData={state}></Preview>
        </GridLayoutPreview>
      </Layout>
      <ModalConfirmLeavePage
        open={openModalConfirmLeave}
        onClose={() => setOpenModalConfirmLeave(false)}
        onConfirm={handleClickBackAction}
      />
    </CURuleStyled>
  );
}
